import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { UserProvider, useUser, canAccess } from './lib/auth'
import type { Section } from './lib/auth'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import OrdersPage from './pages/OrdersPage'
import DesignPage from './pages/DesignPage'
import ProduktionPage from './pages/ProduktionPage'
import OrderDetailPage from './pages/OrderDetailPage'
import NewOrderPage from './pages/NewOrderPage'
import InvoicesPage from './pages/InvoicesPage'
import InvoiceDetailPage from './pages/InvoiceDetailPage'
import ClientsPage from './pages/ClientsPage'
import FlagsPage from './pages/FlagsPage'
import UsersPage from './pages/UsersPage'
import ArchivePage from './pages/ArchivePage'

const ManagementPage = lazy(() => import('./pages/ManagementPage'))

const DEV_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()

  if (DEV_BYPASS) return <>{children}</>

  if (isLoading) return <div className="loading">Anmeldung läuft…</div>

  if (!isAuthenticated) {
    loginWithRedirect()
    return null
  }

  return <>{children}</>
}

function RoleRoute({ children, section }: { children: React.ReactNode; section: Section }) {
  const { user, loading } = useUser()
  if (loading) return <div className="loading">Laden…</div>
  if (!canAccess(user, section)) return <Navigate to="/orders" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <UserProvider>
                <Layout />
              </UserProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/orders" replace />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/new" element={<NewOrderPage />} />
          <Route path="orders/:id" element={<OrderDetailPage />} />
          <Route path="design" element={<DesignPage />} />
          <Route path="produktion" element={<ProduktionPage />} />
          <Route path="invoices" element={<RoleRoute section="invoices"><InvoicesPage /></RoleRoute>} />
          <Route path="invoices/:id" element={<RoleRoute section="invoices"><InvoiceDetailPage /></RoleRoute>} />
          <Route path="clients" element={<RoleRoute section="clients"><ClientsPage /></RoleRoute>} />
          <Route path="flags" element={<RoleRoute section="flags"><FlagsPage /></RoleRoute>} />
          <Route path="users" element={<RoleRoute section="users"><UsersPage /></RoleRoute>} />
          <Route path="archive" element={<RoleRoute section="archive"><ArchivePage /></RoleRoute>} />
          <Route path="management" element={<RoleRoute section="management"><Suspense fallback={<div className="loading">Laden…</div>}><ManagementPage /></Suspense></RoleRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/orders" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
